import type { AxiosResponse, ResponseType } from 'axios';
import debug from 'debug';
import * as z from 'zod';
import * as ba from './base';
import { SCRIPT_SCHEMA } from '../scripting';
import { isComponentContext, assertVueComponent, imageData } from '../helpers';


const log = debug('itp:core:actions:request');


export interface RequestActionArgs {
  url?: string;
  method?: string;
  operation?: string;
  data?: any;
  form?: any;
  parameters?: Dictionary<any>;
  responseType?: ResponseType;
  responseConverter?: string;
  headers?: Dictionary<any>;

  /**
   * override the operation's default content-type
   */
  contentType?: string;
}

export class RequestAction extends ba.Action<RequestActionArgs, any> {
  static get manifest() {
    return {
      name: 'request',
      schema: z.object({
        url: z.string().optional(),
        method: z.string().optional(),
        operation: z.string().optional(),
        data: z.any().optional(),
        form: z.any().optional(),
        parameters: z.union([SCRIPT_SCHEMA(), z.record(z.string(),z.any())]).optional(),
        responseType: z.string().optional(),
        responseConverter: z.string().optional(),
        contentType: z.string().optional(),
        headers: z.record(z.string(), z.any()).optional(),
      })
    };
  }

  async handle(event: ba.ActionEvent, args: RequestActionArgs) {
    log('handling event: %o; args: %o', event, args);

    const component = event.component;
    assertVueComponent(component);

    let content = args.data,
      contentType = args.contentType,
      parameters = args.parameters,
      responseType = args.responseType,
      headers = args.headers,
      method = args.method;

    const isBase64ImageDataRequest = args.responseConverter === 'base64imagedata';
    if (isBase64ImageDataRequest) {
      responseType = 'arraybuffer';
    }

    const response = args.operation ?
      await component.$app.connections.request(args.operation, { content, parameters, contentType, responseType, headers }) :
      await component.$app.connections.ajax(args.url!, { content, parameters, contentType, responseType, headers, method })

    await this.dispatch('success', { response: onResponse(response, isBase64ImageDataRequest) });
    return response;
  }
}


function onResponse(response: AxiosResponse, isBase64ImageDataRequest: boolean) {
  if (response.status === 204) {
    return { ...response, data: null };
  }
  if (response.status === 200 && isBase64ImageDataRequest) {
    return { ...response, data: imageData(response) };
  }
  return response;
}
